<template>
  <div class="row q-col-gutter-md">
    <div class="col-12 col-sm-6">
      <hr-text-field
        :model-value="draft"
        :label="i18n.t('provider.npiLabel')"
        :hint="hint"
        :readonly="readonly"
        :rules="rules"
        lazy-rules
        data-e2e="input-provider-npi"
        clearable
        @update:model-value="onInput"
      />
    </div>
  </div>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import { HrTextField } from 'src/design-system';
import { computed, toRef } from 'vue';

import { useProviderIdentity } from '../composables/useProviderIdentity';

/**
 * The billing identity of the person a staff record describes.
 *
 * IT DOES NOT WRITE. The NPI lives on the very document the host dialog is already saving, so a
 * write from here would be a second writer against a record being edited, and the host's own save
 * would put its stale copy back over the top. So this emits a patch in the document's vocabulary and
 * the host merges it into the draft it already holds. One writer, and the host's existing save is it.
 *
 * IT TAKES THE STORED SHAPE AND EMITS THE STORED SHAPE, which is what makes it the boundary rather
 * than something the host has to translate across. The host passes the person record it is editing
 * and merges the patch it gets back; it never sees a Provider or an Npi, and never imports the
 * mapper. Everything in between belongs to this context.
 *
 * THE CHECK DIGIT WARNS, IT DOES NOT REFUSE. A number of the wrong shape is not an NPI and is
 * refused outright. A ten-digit number whose check digit does not match is almost always a typo and
 * occasionally a payer's own record being odd, so it is surfaced and still saved: refusing it would
 * make the field unfillable for the one person who knows better, and every claim it produced would
 * have failed visibly anyway.
 *
 * THE WARNING RIDES THE HINT, which is the field's own bottom line and where the reader is already
 * looking. The error state is a refusal and would say the value is wrong while the save stores it
 * anyway; a banner is a section-level component and is a coarser instrument than one field's soft
 * validation. What is missing is a field-level warning state on the text field, and inventing one
 * here would be a design-system decision taken in a feature.
 */

const props = defineProps({
  /**
   * The stored person record the host is editing, in the platform's own shape. Null while the record
   * does not exist yet.
   */
  person: {
    type: Object,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /**
   * Raised with a patch to merge into the host's draft. Payload is `{ npi: string|null }`, where
   * null means the reader cleared it and expects it gone.
   */
  change: null,
});

const {
  draft, isRefused, isSuspect, currentPatch,
} = useProviderIdentity(toRef(props, 'person'));

const hint = computed(() => {
  return i18n.t(isSuspect.value ? 'provider.npiCheckDigitWarning' : 'provider.npiHint');
});

/**
 * A rule rather than the `error` prop, because the host validates the whole form before saving and
 * a forced `error` is invisible to that. Without this the reader sees the field marked wrong, saves
 * anyway, and the record silently keeps the number they thought they had replaced.
 *
 * It ignores the value Quasar hands it and asks the composable instead: the draft is already the
 * single source of what is in the field, and a second reading of the same string is a second place
 * for the two to disagree.
 */
const rules = [() => !isRefused.value || i18n.t('provider.npiMalformed')];

function onInput(value) {
  draft.value = value ?? '';

  const patch = currentPatch();

  if (patch !== null) emit('change', patch);
}
</script>
